import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { PhotoIcon } from '@heroicons/react/24/solid';
import ActionButton from './ActionButton';
import styles from './Form.module.css';
import SimpleFileUpload from './SimpleFileUpload';
import ColorPicker from './ColorPicker';

// Types and Interfaces
interface HelpIconProps {
  helpUrl?: string;
}

export interface FormField {
  name: string;
  type:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'url'
    | 'tel'
    | 'time'
    | 'date'
    | 'textarea'
    | 'select'
    | 'checkbox'
    | 'radio'
    | 'multi-checkbox'
    | 'product-multi-select'
    | 'file'
    | 'color'
    | 'custom';
  label: string;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  disabled?: boolean;
  showHelp?: boolean;
  helpUrl?: string;
  options?: Array<{ value: string; label: string; description?: string }> | string[];
  accept?: string;
  multiple?: boolean;
  component?: React.ComponentType<any>;
  props?: Record<string, any>;
  fileNote?: string;
  maxFileSizeBytes?: number;
  fileTypeError?: string;
  condition?: (formData: any) => boolean;
}

interface FieldComponentProps {
  field: FormField;
  value: any;
  onChange: (fieldName: string, value: any) => void;
  onBlur?: (fieldName: string) => void;
  error?: string;
  refs?: Record<string, React.RefObject<any>>;
  formData?: Record<string, any>;
}

interface FormProps {
  schema?: FormField[];
  initialData?: Record<string, any>;
  onSubmit?: (data: Record<string, any>, event?: React.FormEvent) => void;
  onFieldChange?: (fieldName: string, value: any, newFormData: Record<string, any>) => void;
  validationErrors?: Record<string, string>;
  isLoading?: boolean;
  submitButtonText?: string;
  submitButtonProps?: Record<string, any>;
  className?: string;
  formProps?: Record<string, any>;
  twoColumn?: boolean;
  leftColumnFields?: string[];
  rightColumnFields?: string[];
}

// Help Icon Component
const HelpIcon: React.FC<HelpIconProps> = ({ helpUrl = '/docs' }) => (
  <a href={helpUrl} target="_blank" rel="noopener noreferrer" className={styles.helpIconLink}>
    <QuestionMarkCircleIcon className={styles.helpIcon} />
  </a>
);

// Individual Field Components
const TextField: React.FC<FieldComponentProps> = React.memo(
  ({ field, value, onChange, onBlur, error, refs }: FieldComponentProps) => (
    <div className={styles.formGroup}>
      <label htmlFor={field.name} className={styles.formLabel}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </label>
      <input
        ref={refs?.[field.name]}
        type={field.type || 'text'}
        id={field.name}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        onBlur={() => onBlur?.(field.name)}
        onWheel={field.type === 'number' ? (e) => e.currentTarget.blur() : undefined}
        className={`${styles.formInput} ${error ? styles.formInputError : ''}`}
        placeholder={field.placeholder}
        required={field.required}
        disabled={field.disabled}
        minLength={field.minLength}
        maxLength={field.maxLength}
        min={field.min}
        max={field.max}
        step={field.step}
      />
      {error && (
        <p className={styles.formErrorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
TextField.displayName = 'TextField';

const TextAreaField: React.FC<FieldComponentProps> = React.memo(
  ({ field, value, onChange, onBlur, error, refs }: FieldComponentProps) => (
    <div className={styles.formGroup}>
      <label htmlFor={field.name} className={styles.formLabel}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </label>
      <textarea
        ref={refs?.[field.name]}
        id={field.name}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        onBlur={() => onBlur?.(field.name)}
        rows={field.rows || 4}
        className={`${styles.formTextarea} ${error ? styles.formInputError : ''}`}
        placeholder={field.placeholder}
        required={field.required}
        disabled={field.disabled}
      />
      {error && (
        <p className={styles.formErrorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
TextAreaField.displayName = 'TextAreaField';

const FileField: React.FC<FieldComponentProps> = React.memo(
  ({ field, value, onChange, error }: FieldComponentProps) => (
    <div className={styles.formGroup}>
      <SimpleFileUpload
        label={field.label}
        accept={field.accept || 'image/*'}
        multiple={field.multiple || false}
        required={field.required || false}
        fileNote={field.fileNote || 'Upload image file'}
        value={value || null}
        onChange={(files: File | File[] | null) => onChange(field.name, files)}
        error={error}
      />
    </div>
  ),
);
FileField.displayName = 'FileField';

const ColorField: React.FC<FieldComponentProps> = React.memo(
  ({ field, value, onChange, error }: FieldComponentProps) => (
    <ColorPicker
      label={field.label}
      value={value}
      onChange={(v: string) => onChange(field.name, v)}
      required={field.required}
      disabled={field.disabled}
      placeholder={field.placeholder}
      error={error}
    />
  ),
);
ColorField.displayName = 'ColorField';

const SelectField: React.FC<FieldComponentProps> = React.memo(
  ({ field, value, onChange, onBlur, error, refs }: FieldComponentProps) => (
    <div className={styles.formGroup}>
      <label htmlFor={field.name} className={styles.formLabel}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </label>
      <select
        ref={refs?.[field.name]}
        id={field.name}
        name={field.name}
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        onBlur={() => onBlur?.(field.name)}
        className={`${styles.formSelect} ${error ? styles.formInputError : ''}`}
        required={field.required}
        disabled={field.disabled}
      >
        <option value="">{field.placeholder || 'Select an option'}</option>
        {field.options?.map((option, index) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          return (
            <option key={optionValue || index} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
      {error && (
        <p className={styles.formErrorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
SelectField.displayName = 'SelectField';

const CheckboxField: React.FC<FieldComponentProps> = ({
  field,
  value,
  onChange,
  error,
}: FieldComponentProps) => (
  <div className={styles.formGroup}>
    <label className={styles.customSwitchContainer}>
      <span className={styles.customSwitchLabel}>
        {field.label}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </span>
      <div className={styles.customSwitch}>
        <input
          type="checkbox"
          name={field.name}
          checked={value || false}
          onChange={(e) => onChange(field.name, e.target.checked)}
          disabled={field.disabled}
        />
        <span className={styles.switchSlider}></span>
      </div>
    </label>
    {error && (
      <p className={styles.formErrorMessage} role="alert">
        {error}
      </p>
    )}
  </div>
);

const RadioField: React.FC<FieldComponentProps> = ({
  field,
  value,
  onChange,
  error,
}: FieldComponentProps) => (
  <div className={styles.formGroup}>
    <fieldset className={styles.radioGroup}>
      <legend className="sr-only">{field.label}</legend>
      <div className={styles.formLabel}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </div>
      <div className={styles.radioContainer}>
        {field.options?.map((option, index) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          const optionDescription = typeof option === 'object' ? option.description : undefined;
          return (
            <label
              key={optionValue || index}
              className={`${styles.radioItem} ${value === optionValue ? styles.checked : ''}`}
            >
              <input
                type="radio"
                name={field.name}
                value={optionValue}
                checked={value === optionValue}
                onChange={(e) => onChange(field.name, e.target.value)}
                className={styles.radioInput}
                disabled={field.disabled}
              />
              <span className={styles.radioCircle} />
              <span className={styles.radioLabelContainer}>
                <span className={styles.radioLabel}>{optionLabel}</span>
                {optionDescription && (
                  <span className={styles.radioDescription}>{optionDescription}</span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
    {error && (
      <p className={styles.formErrorMessage} role="alert">
        {error}
      </p>
    )}
  </div>
);

const MultiCheckboxField: React.FC<FieldComponentProps> = ({
  field,
  value,
  onChange,
  error,
}: FieldComponentProps) => {
  const handleChange = (optionValue: string, checked: boolean) => {
    const currentValues = Array.isArray(value) ? value : [];
    if (checked) {
      onChange(field.name, [...currentValues, optionValue]);
    } else {
      onChange(
        field.name,
        currentValues.filter((v) => v !== optionValue),
      );
    }
  };

  return (
    <div className={styles.formGroup}>
      <div className={styles.formLabel}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </div>
      <div className={styles.multiCheckboxContainer}>
        {field.options?.map((option, index) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          return (
            <label key={optionValue || index} className={styles.customSwitchContainer}>
              <span className={styles.customSwitchLabel}>{optionLabel}</span>
              <div className={styles.customSwitch}>
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(optionValue)}
                  onChange={(e) => handleChange(optionValue, e.target.checked)}
                  disabled={field.disabled}
                />
                <span className={styles.switchSlider}></span>
              </div>
            </label>
          );
        })}
      </div>
      {error && (
        <p className={styles.formErrorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

const ProductMultiSelectField: React.FC<FieldComponentProps> = ({
  field,
  value,
  onChange,
  error,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = (optionValue: string, checked: boolean) => {
    const currentValues = Array.isArray(value) ? value : [];
    if (checked) {
      onChange(field.name, [...currentValues, optionValue]);
    } else {
      onChange(
        field.name,
        currentValues.filter((v) => v !== optionValue),
      );
    }
  };

  const handleSelectAll = () => {
    const allValues =
      field.options?.map((option) => (typeof option === 'string' ? option : option.value)) || [];
    onChange(field.name, allValues);
  };

  const handleDeselectAll = () => {
    onChange(field.name, []);
  };

  const filteredOptions =
    field.options?.filter((option) => {
      const label = typeof option === 'string' ? option : option.label;
      return label.toLowerCase().includes(searchTerm.toLowerCase());
    }) || [];

  const selectedCount = Array.isArray(value) ? value.length : 0;
  const totalCount = field.options?.length || 0;

  return (
    <div className={styles.formGroup}>
      <div className={styles.formLabel}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </div>

      <div className={styles.productMultiSelectContainer}>
        {/* Header with selection info and controls */}
        <div className={styles.productMultiSelectHeader}>
          <div className={styles.productMultiSelectInfo}>
            <span className={styles.productMultiSelectCount}>
              {selectedCount} of {totalCount} products selected
            </span>
          </div>
          <div className={styles.productMultiSelectControls}>
            <button
              type="button"
              onClick={handleSelectAll}
              className={styles.productMultiSelectControlBtn}
              disabled={field.disabled}
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              className={styles.productMultiSelectControlBtn}
              disabled={field.disabled}
            >
              Clear All
            </button>
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className={styles.productMultiSelectToggle}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </button>
          </div>
        </div>

        {/* Search input */}
        <div className={styles.productMultiSelectSearch}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.productMultiSelectSearchInput}
            disabled={field.disabled}
          />
        </div>

        {/* Product grid */}
        <div className={`${styles.productMultiSelectGrid} ${isExpanded ? styles.expanded : ''}`}>
          {filteredOptions.map((option, index) => {
            const optionValue = typeof option === 'string' ? option : option.value;
            const optionLabel = typeof option === 'string' ? option : option.label;
            const optionImage = typeof option === 'object' ? (option as any).image : undefined;
            const isSelected = Array.isArray(value) && value.includes(optionValue);
            return (
              <div
                key={optionValue || index}
                className={`${styles.productCard} ${isSelected ? styles.selected : ''}`}
                onClick={() => !field.disabled && handleChange(optionValue, !isSelected)}
              >
                <div className={styles.productCardImageContainer}>
                  {optionImage && (
                    <img
                      src={optionImage}
                      alt={optionLabel}
                      className={styles.productCardImage}
                      width={60}
                      height={60}
                    />
                  )}
                  {isSelected && <div className={styles.productCardSelectedCheck}>✔</div>}
                </div>
                <div className={styles.productCardInfo}>
                  <p className={styles.productCardLabel}>{optionLabel}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show selected products summary */}
        {selectedCount > 0 && (
          <div className={styles.productMultiSelectSummary}>
            <div className={styles.productMultiSelectSummaryTitle}>
              Selected Products ({selectedCount}):
            </div>
            <div className={styles.productMultiSelectSummaryItems}>
              {field.options
                ?.filter((option) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  return Array.isArray(value) && value.includes(optionValue);
                })
                .map((option, index) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label;
                  return (
                    <span
                      key={optionValue || index}
                      className={styles.productMultiSelectSummaryItem}
                    >
                      {optionLabel}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChange(optionValue, false);
                        }}
                        className={styles.productMultiSelectSummaryRemove}
                        disabled={field.disabled}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className={styles.formErrorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

const CustomField: React.FC<FieldComponentProps> = ({
  field,
  value,
  onChange,
  error,
  formData,
  refs,
}) => {
  const Component = field.component;
  if (!Component) return null;

  return (
    <div className={styles.formGroup}>
      {field.label && (
        <div className={styles.formLabel}>
          {field.label}
          {field.required && <span className={styles.formRequired}>*</span>}
          {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
        </div>
      )}
      {/* Render the component - it can be either a React component or a function that returns JSX */}
      <Component
        field={field}
        value={value}
        onChange={onChange}
        formData={formData}
        {...field.props}
      />
      {error && (
        <p className={styles.formErrorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

const DateField: React.FC<FieldComponentProps> = React.memo(
  ({ field, value, onChange, onBlur, error, refs }: FieldComponentProps) => (
    <div className={styles.formGroup}>
      <label htmlFor={field.name} className={styles.formLabel}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
        {field.showHelp && <HelpIcon helpUrl={field.helpUrl} />}
      </label>
      <div className={styles.dateInputContainer}>
        <input
          ref={refs?.[field.name]}
          type="date"
          id={field.name}
          name={field.name}
          value={value || ''}
          onChange={(e) => onChange(field.name, e.target.value)}
          onBlur={() => onBlur?.(field.name)}
          className={`${styles.formInput} ${styles.dateInput} ${error ? styles.formInputError : ''}`}
          required={field.required}
          disabled={field.disabled}
          min={field.min?.toString()}
          max={field.max?.toString()}
        />
        <div className={styles.dateInputIcon}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>
      </div>
      {error && (
        <p className={styles.formErrorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
DateField.displayName = 'DateField';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Schema-driven validation for a single field. Returns an error string ('' = valid).
// Used for both on-blur feedback and the on-submit gate so the rules stay in one place.
const isFileValue = (value: unknown): value is File =>
  typeof File !== 'undefined' && value instanceof File;

const matchesAcceptedFileType = (file: File, accept?: string): boolean => {
  if (!accept || accept.trim() === '' || accept.trim() === '*') {
    return true;
  }

  const acceptedTypes = accept
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const mimeType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  return acceptedTypes.some((acceptedType) => {
    if (acceptedType.startsWith('.')) {
      return fileName.endsWith(acceptedType);
    }
    if (acceptedType.endsWith('/*')) {
      return mimeType.startsWith(acceptedType.slice(0, -1));
    }
    return mimeType === acceptedType;
  });
};

const formatFileSize = (bytes: number): string => {
  const megabytes = bytes / (1024 * 1024);
  return Number.isInteger(megabytes) ? `${megabytes} MB` : `${megabytes.toFixed(1)} MB`;
};

export const validateField = (
  field: FormField,
  value: unknown,
  formData: Record<string, any>,
): string => {
  // Conditionally hidden fields are never validated.
  if (field.condition && !field.condition(formData)) {
    return '';
  }

  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0);

  if (field.required && isEmpty) {
    return `${field.label} is required`;
  }

  // Nothing further to check for empty optional fields.
  if (isEmpty) {
    return '';
  }

  if (field.type === 'file') {
    // A non-empty string represents an already uploaded file in edit forms.
    const files = Array.isArray(value)
      ? value.filter(isFileValue)
      : isFileValue(value)
        ? [value]
        : [];

    for (const file of files) {
      if (!matchesAcceptedFileType(file, field.accept)) {
        return field.fileTypeError || `${field.label} has an unsupported file format`;
      }

      if (typeof field.maxFileSizeBytes === 'number' && file.size > field.maxFileSizeBytes) {
        return `${field.label} must be ${formatFileSize(field.maxFileSizeBytes)} or smaller`;
      }

      if (file.size === 0) {
        return `${field.label} cannot be empty`;
      }
    }

    return '';
  }

  if (field.type === 'number') {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return `${field.label} must be a valid number`;
    }
    if (typeof field.min === 'number' && numericValue < field.min) {
      return `${field.label} must be at least ${field.min}`;
    }
    if (typeof field.max === 'number' && numericValue > field.max) {
      return `${field.label} must be at most ${field.max}`;
    }
    if (
      typeof field.step === 'number' &&
      Number.isInteger(field.step) &&
      !Number.isInteger(numericValue)
    ) {
      return `${field.label} must be a whole number`;
    }
  }

  if (typeof value !== 'string') {
    return '';
  }

  if (field.type === 'email' && !EMAIL_PATTERN.test(value.trim())) {
    return 'Please enter a valid email address';
  }

  if (typeof field.minLength === 'number' && value.length < field.minLength) {
    return `${field.label} must be at least ${field.minLength} characters`;
  }

  if (typeof field.maxLength === 'number' && value.length > field.maxLength) {
    return `${field.label} must be at most ${field.maxLength} characters`;
  }

  return '';
};

// Main Form Component
const Form: React.FC<FormProps> = ({
  schema = [],
  initialData = {},
  onSubmit,
  onFieldChange,
  validationErrors = {},
  isLoading = false,
  submitButtonText = 'Submit',
  submitButtonProps = {},
  className = '',
  formProps = {},
  twoColumn = false,
  leftColumnFields = [],
  rightColumnFields = [],
}) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const areShallowEqual = (left: Record<string, any>, right: Record<string, any>) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    return leftKeys.every((key) => Object.is(left[key], right[key]));
  };

  // Effect to sync formData with initialData when it changes
  useEffect(() => {
    setFormData((previousFormData) => {
      if (
        areShallowEqual(previousFormData as Record<string, any>, initialData as Record<string, any>)
      ) {
        return previousFormData;
      }

      return initialData;
    });
  }, [initialData]);

  // Live mirrors of state/props so the change & blur handlers can stay referentially
  // stable (empty/[onFieldChange] deps). Without this, every keystroke would hand each
  // memoized field a brand-new onChange and re-render the entire form.
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  // Memoize refs for stability
  const fieldNames = useMemo(() => schema.map((field) => field.name), [schema]);
  const refs = useMemo(() => {
    const fieldRefs: Record<string, React.RefObject<any>> = {};
    fieldNames.forEach((name) => {
      fieldRefs[name] = React.createRef();
    });
    return fieldRefs;
  }, [fieldNames]);

  const focusFirstError = useCallback(
    (fieldErrors: Record<string, string>) => {
      const firstErrorField = Object.keys(fieldErrors)[0];
      if (firstErrorField && refs[firstErrorField]?.current) {
        refs[firstErrorField].current?.focus();
      }
    },
    [refs],
  );

  // Effect to focus on the first field with a parent-supplied error
  useEffect(() => {
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      focusFirstError(validationErrors);
    }
  }, [validationErrors, focusFirstError]);

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      const newFormData = { ...formDataRef.current, [fieldName]: value };
      formDataRef.current = newFormData;
      setFormData(newFormData);

      // File inputs do not have a useful blur event, so validate a selection
      // immediately. Other fields clear their old error while the user edits.
      setErrors((prev) => {
        const field = schemaRef.current.find((item) => item.name === fieldName);
        const fileError = field?.type === 'file' ? validateField(field, value, newFormData) : '';

        if (fileError) {
          return { ...prev, [fieldName]: fileError };
        }
        if (!prev[fieldName]) {
          return prev;
        }
        const { [fieldName]: removed, ...rest } = prev;
        return rest;
      });

      // Call external change handler if provided
      if (onFieldChange) {
        onFieldChange(fieldName, value, newFormData);
      }
    },
    [onFieldChange],
  );

  // Validate a single field on blur so empty/invalid required fields are highlighted
  // immediately, before the user ever reaches the submit button.
  const handleFieldBlur = useCallback((fieldName: string) => {
    const field = schemaRef.current.find((item) => item.name === fieldName);
    if (!field) {
      return;
    }

    const message = validateField(field, formDataRef.current[fieldName], formDataRef.current);

    setErrors((prev) => {
      if (message) {
        if (prev[fieldName] === message) {
          return prev;
        }
        return { ...prev, [fieldName]: message };
      }
      if (!prev[fieldName]) {
        return prev;
      }
      const { [fieldName]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate everything up front so all invalid fields highlight at once.
    const data = formDataRef.current;
    const nextErrors: Record<string, string> = {};
    schemaRef.current.forEach((field) => {
      const message = validateField(field, data[field.name], data);
      if (message) {
        nextErrors[field.name] = message;
      }
    });

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(nextErrors);
      return;
    }

    if (onSubmit) {
      onSubmit(data, e);
    }
  };

  const renderField = (field: FormField) => {
    // Check if field has a condition and evaluate it
    if (field.condition && !field.condition(formData)) {
      return null;
    }

    const value = formData[field.name];
    const error = errors[field.name] || validationErrors[field.name];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'number':
      case 'url':
      case 'tel':
      case 'time':
        return (
          <TextField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={error}
            refs={refs}
          />
        );

      case 'date':
        return (
          <DateField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={error}
            refs={refs}
          />
        );

      case 'textarea':
        return (
          <TextAreaField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={error}
            refs={refs}
          />
        );

      case 'select':
        return (
          <SelectField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={error}
            refs={refs}
          />
        );

      case 'checkbox':
        return (
          <CheckboxField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            error={error}
          />
        );

      case 'radio':
        return (
          <RadioField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            error={error}
          />
        );

      case 'multi-checkbox':
        return (
          <MultiCheckboxField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            error={error}
          />
        );

      case 'product-multi-select':
        return (
          <ProductMultiSelectField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            error={error}
          />
        );

      case 'file':
        return (
          <FileField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            error={error}
          />
        );

      case 'color':
        return (
          <ColorField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            error={error}
          />
        );

      case 'custom':
        return (
          <CustomField
            key={field.name}
            field={field}
            value={value}
            onChange={handleFieldChange}
            error={error}
            formData={formData}
            refs={refs}
          />
        );

      default:
        return null;
    }
  };

  const renderLeftColumn = () => {
    return leftColumnFields.map((fieldName) => {
      const field = schema.find((item) => item.name === fieldName);
      return field ? renderField(field) : null;
    });
  };

  const renderRightColumn = () => {
    return rightColumnFields.map((fieldName) => {
      const field = schema.find((item) => item.name === fieldName);
      return field ? renderField(field) : null;
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`${styles.form} ${twoColumn ? styles.formTwoColumn : ''} ${className}`}
      noValidate
      {...formProps}
    >
      {twoColumn ? (
        <div className={styles.formColumnsContainer}>
          <div className={styles.formLeftColumn}>{renderLeftColumn()}</div>
          <div className={styles.formRightColumn}>{renderRightColumn()}</div>
        </div>
      ) : (
        schema.map((field) => renderField(field))
      )}

      {onSubmit && (
        <div className={`${styles.formActions} ${twoColumn ? styles.formActionsTwoColumn : ''}`}>
          <ActionButton
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
            {...submitButtonProps}
          >
            {isLoading ? 'Submitting...' : submitButtonText}
          </ActionButton>
        </div>
      )}
    </form>
  );
};

export default Form;
